/**
 * Clients, on the shared list engine — `docs/rowted-admin-v2.md` §2.8, §5.5.
 *
 * This file used to be a table. A search box, a kind dropdown, a `<table>`, a
 * header row, a loading state and an empty state — perhaps 140 lines of
 * things the office app had already solved once. What replaced them is
 * `ListScreen` over a platform `AreaDef`, and everything the board draws comes
 * with it: saved views as the page-bar tabs, the four stat cards, the quick
 * filter and the advanced builder, grouping, the Σ footer, column dragging and
 * pinning, Export.
 *
 * The only thing this screen says that a tenant list does not is **which
 * fetch**. `adminRequest` goes to `/api/v1/platform` with the platform CSRF
 * header, and passing it to `useList` and `ListScreen` is the whole of the
 * difference — the server side of that seam is `routes/list-runner.ts`.
 */
import { useCallback, useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import type { ListRow } from '../../lib/use-list.js'
import { useList } from '../../lib/use-list.js'
import { adminRequest } from '../../lib/admin-api.js'
import { ListScreen } from '../../components/list-screen.js'
import { Button, Tag, type TagTone } from '../../components/primitives.js'
import { Toast } from '../../components/overlays.js'
import { IconPlus } from '../../frame/icons.js'
import {
  NewClientFlyout, type NewClientResult, type Tier,
} from '../../components/admin/new-client-flyout.js'
import { adminRequest as ask } from '../../lib/admin-api.js'
import { formatRelative } from '../../lib/format.js'
import { AdminPage, can, useAdminUser } from './shell.js'
import { useStepUp } from './step-up.js'

/** What each health word looks like. Board 8's tags, in the app's own tones. */
const HEALTH: Record<string, { label: string; tone: TagTone }> = {
  healthy: { label: 'Healthy', tone: 'ok' },
  card_needed: { label: 'Card needed', tone: 'warn' },
  quiet: { label: 'Quiet', tone: 'quiet' },
  demo: { label: 'Demo', tone: 'plain' },
  read_only: { label: 'Read only', tone: 'bad' },
}

const KIND: Record<string, { label: string; tone: TagTone }> = {
  production: { label: 'Production', tone: 'ok' },
  trial: { label: 'Trial', tone: 'warn' },
  demo: { label: 'Demo', tone: 'plain' },
}

export function AdminClients() {
  const { me } = useAdminUser()
  const navigate = useNavigate()
  /* Creating a client is a step-up; the flyout runs its submit through this. */
  const stepUp = useStepUp()
  const [making, setMaking] = useState(false)
  const location = useLocation()
  /*
   * A client's page that has just removed its client has nowhere to say so —
   * the page is gone — so it comes back here carrying the sentence.
   */
  const [notice, setNotice] = useState<string | null>(
    () => (location.state as { notice?: string } | null)?.notice ?? null)
  /*
   * The price list and the trial length, so the flyout opens on what a signup
   * would get rather than on a number typed into this file. Fetched once when
   * the screen loads, not when the flyout opens — a form that has to wait for
   * a settings read before it can draw its Plan field is a form that flickers.
   */
  const [tiers, setTiers] = useState<Tier[]>([])
  const [trialDays, setTrialDays] = useState(14)

  const list = useList('clients', {
    /* The row menu needs the slug for its label, shown or not. */
    alwaysFetch: ['slug', 'status'],
    request: adminRequest,
  })

  const mayMake = can(me, 'clients')

  useEffect(() => {
    if (!mayMake) return
    /*
     * `{ settings: … }`, not the settings themselves — the same envelope the
     * Settings page unwraps. Reading it flat gave the flyout an empty Plan
     * field and no error anywhere, which is how a wrong shape usually shows
     * up in a browser.
     */
    void ask<{
      settings: { tiers: { tiers: Tier[] }; trials: { days: number } }
    }>('/settings').then(({ settings }) => {
      setTiers(settings.tiers.tiers)
      setTrialDays(settings.trials.days)
    }).catch(() => { /* the flyout copes with an empty plan list */ })
  }, [mayMake])

  const made = useCallback((result: NewClientResult, signIn: boolean) => {
    setMaking(false)
    if (signIn) {
      /*
       * Straight into the new company as its owner. A full page load rather
       * than a route change: the browser is now carrying a tenant cookie as
       * well as the console one, and the office app has to boot from scratch.
       */
      window.location.assign(`/admin/clients/${result.tenantId}?signInAs=1`)
      return
    }
    setNotice(result.invited
      ? 'Made, and the owner has been emailed.'
      : 'Made. Nobody has been emailed yet.')
    void list.reload()
    navigate(`/admin/clients/${result.tenantId}`)
  }, [list, navigate])

  return (
    <AdminPage me={me} active="clients">
      <ListScreen
        area="clients"
        title="Clients"
        chrome="page"
        cards
        list={list}
        request={adminRequest}
        primaryColumn={{
          field: 'name',
          header: 'Client',
          render: (row: ListRow) => ({
            primary: String(row.name ?? ''),
            secondary: [
              row.slug,
              row.since ? `since ${String(row.since).slice(0, 4)}` : null,
            ].filter(Boolean).join(' · ') || undefined,
          }),
        }}
        cell={{
          health: (row) => {
            const health = HEALTH[String(row.health ?? '')]
            return health ? <Tag tone={health.tone}>{health.label}</Tag> : null
          },
          kind: (row) => {
            const kind = KIND[String(row.kind ?? '')]
            return kind ? <Tag tone={kind.tone}>{kind.label}</Tag> : null
          },
          lastActiveAt: (row) => (row.lastActiveAt
            ? formatRelative(String(row.lastActiveAt))
            : <span className="text-text-faint">never</span>),
        }}
        onRowClick={(row) => navigate(`/admin/clients/${String(row.id)}`)}
        rowMenu={(row) => [
          { key: 'open', label: 'Open', onSelect: () => navigate(`/admin/clients/${String(row.id)}`) },
          /*
           * Signing in as somebody is behind `signInAs` — §2.3 gives it to
           * owner, admin and support and to nobody else. Hidden rather than
           * shown-and-refused: `shared/src/platform-roles.ts` is the same
           * table the server checks, so the two cannot disagree.
           *
           * It opens the client's own record with the dialog asked for, rather
           * than starting anything from here: §5.5 wants a reason, a consent
           * flag and a duration recorded first, and a row menu is not where
           * that conversation belongs.
           */
          ...(can(me, 'signInAs')
            ? [{
                key: 'sign-in-as',
                label: 'Sign in as…',
                onSelect: () => navigate(`/admin/clients/${String(row.id)}?signInAs=1`),
              }]
            : []),
        ]}
        {...(mayMake
          ? {
            actions: (
              <Button variant="primary" onClick={() => setMaking(true)}>
                <IconPlus size={15} />
                New client
              </Button>
            ),
          }
          : {})}
        emptyTitle="No clients yet"
        emptyBody="A trial signup or an import is what makes the first one."
      />

      {making && (
        <NewClientFlyout
          tiers={tiers}
          trialDays={trialDays}
          guard={stepUp.guard}
          onClose={() => setMaking(false)}
          onMade={made}
        />
      )}
      {notice && (
        <Toast tone="success" onDismiss={() => setNotice(null)}>{notice}</Toast>
      )}
      {stepUp.dialog}
    </AdminPage>
  )
}
